import {
  createCsrfMiddleware,
  createMiddleware,
  createStart,
} from "@tanstack/solid-start";

function shouldCaptureSentryException(error: unknown): boolean {
  if (error instanceof Response) {
    // TanStack middleware/auth commonly throws Response for redirects and
    // expected HTTP control flow; these should not pollute error tracking.
    return false;
  }

  return true;
}

/**
 * Captures unhandled errors thrown by server route handlers (HTTP requests).
 * Uses a dynamic import so @sentry/node is never bundled into the client.
 */
const sentryRequestMiddleware = createMiddleware({ type: "request" }).server(
  async ({ next, request }) => {
    try {
      return await next();
    } catch (error) {
      if (shouldCaptureSentryException(error)) {
        const Sentry = await import("@sentry/node");
        Sentry.withScope((scope) => {
          const url = new URL(request.url).pathname;
          scope.setExtra("url", url);
          scope.setExtra("method", request.method);
          Sentry.captureException(error);
        });
      }
      throw error;
    }
  },
);

/**
 * Captures unhandled errors thrown by createServerFn calls.
 * Uses a dynamic import so @sentry/node is never bundled into the client.
 */
const sentryFunctionMiddleware = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    try {
      return await next();
    } catch (error) {
      if (shouldCaptureSentryException(error)) {
        const Sentry = await import("@sentry/node");
        Sentry.captureException(error);
      }
      throw error;
    }
  },
);

const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) => ctx.handlerType === "serverFn",
});

/**
 * MTA-STS policy (RFC 8460) for mta-sts.actshark.com, served by this app via
 * a DNS-only Cloudflare CNAME. Mail servers parse the body strictly — it must
 * be exact plain text, not an HTML page.
 *
 * Inbound MX is Private Email (Namecheap); outbound ZeptoMail is irrelevant
 * here since MTA-STS only governs delivery TO these MX hosts.
 *
 * Rollout: keep `mode: testing` until TLS-RPT reports (sent to
 * support@actshark.com via the _smtp._tls TXT record) show no failures for a
 * few days, then switch to `mode: enforce` AND bump the id in the `_mta-sts`
 * TXT record (e.g. 2026090401 -> 2026090701). Senders cache the policy for
 * `max_age` seconds, so the DNS id and this file must change together.
 */
const MTA_STS_POLICY = `version: STSv1
mode: enforce
mx: mx1.privateemail.com
mx: mx2.privateemail.com
max_age: 86400
`;

const mtaStsMiddleware = createMiddleware({ type: "request" }).server(
  async ({ next, request }) => {
    const { pathname } = new URL(request.url);
    if (pathname !== "/.well-known/mta-sts.txt") {
      return next();
    }
    if (request.method !== "GET" && request.method !== "HEAD") {
      return new Response("Method Not Allowed\n", {
        status: 405,
        headers: {
          "Content-Type": "text/plain;charset=UTF-8",
          Allow: "GET, HEAD",
        },
      });
    }
    return new Response(MTA_STS_POLICY, {
      status: 200,
      headers: {
        "Content-Type": "text/plain;charset=UTF-8",
        "Cache-Control": "public, max-age=86400",
      },
    });
  },
);

export const startInstance = createStart(() => ({
  requestMiddleware: [
    csrfMiddleware,
    mtaStsMiddleware,
    sentryRequestMiddleware,
  ],
  functionMiddleware: [sentryFunctionMiddleware],
}));
